export interface OrderPreviewProduct {
  sku?: string;
  quantity?: number;
  deliveryChannel?: string;
  lineNumber?: string | number;
  crd?: string;
  CRD_Range?: any;
  product?: any;
  [key: string]: any;
}

const normalizeLineNumber = (lineNumber?: string | number) =>
  lineNumber == null ? undefined : String(lineNumber);

const getProductSku = (product: OrderPreviewProduct) =>
  product.sku || product.product?.sku || product.product?.catalogNumber;

const getProductDeliveryChannel = (product: OrderPreviewProduct) =>
  product.deliveryChannel || product.product?.deliveryChannel;

const getProductLineNumber = (product: OrderPreviewProduct) =>
  normalizeLineNumber(product.lineNumber ?? product.product?.lineNumber);

const getProductAvailabilityDate = (product: OrderPreviewProduct) =>
  product.product?.availabilityDate;

const getProductStockStatus = (product: OrderPreviewProduct) =>
  product.product?.stockStatus;

export interface CRDRangeResponseItem {
  catalogNumber?: string;
  deliveryChannel?: string;
  lineNumber?: string | number;
  defaultDate?: string;
  [key: string]: any;
}

export interface CRDHolidayItem {
  date?: string;
  type?: "public_holiday" | "transfer_workday" | string;
}

export interface CRDRangeResponse {
  crdRanges?: CRDRangeResponseItem[];
  holidays?: CRDHolidayItem[] | string[];
}

export interface CRDSelectionPayload {
  newCrd?: string;
  selectedLinesMap: Record<string, string>;
}

const normalizeDateValue = (value?: string) => {
  if (!value) return "";
  return String(value).split("T")[0].replace(/\//g, "-");
};

const getHolidayDateMap = (holidays: CRDHolidayItem[] | string[] = []) => {
  const holidayMap = new Map<string, string>();
  holidays.forEach((item) => {
    if (typeof item === "string") {
      const date = normalizeDateValue(item);
      if (date) {
        holidayMap.set(date, "public_holiday");
      }
      return;
    }
    const date = normalizeDateValue(item?.date);
    if (date) {
      holidayMap.set(date, item?.type || "");
    }
  });
  return holidayMap;
};

export const getHolidayType = (
  dateStr?: string,
  holidays: CRDHolidayItem[] | string[] = []
) => getHolidayDateMap(holidays).get(normalizeDateValue(dateStr)) || "";

export const isHolidayDisabled = (
  dateStr?: string,
  holidays: CRDHolidayItem[] | string[] = []
) => getHolidayType(dateStr, holidays) === "public_holiday";

export const getOrderLineKey = (
  product: Pick<OrderPreviewProduct, "deliveryChannel" | "lineNumber"> & { product?: any }
) => `${product.deliveryChannel || product.product?.deliveryChannel || ""}${normalizeLineNumber(product.lineNumber ?? product.product?.lineNumber) || ""}`;

export const normalizeOrderLinesForSubmit = (products: OrderPreviewProduct[] = []) =>
  products.map((product) => {
    const { CRD_Range, ...rest } = product;
    return {
      ...rest,
      ...(getProductLineNumber(product) ? { lineNumber: getProductLineNumber(product) } : { lineNumber: null }),
    };
  });

export const buildCRDRangeRequest = ({
  shipToNum,
  supplyCenterId,
  products,
}: {
  shipToNum?: string;
  supplyCenterId?: string | number;
  products?: OrderPreviewProduct[];
}) => ({
  shipToNum,
  supplyCenterId,
  items: (products || [])
    .filter(
      (product) =>
        product &&
        !product.freeGoodItem &&
        getProductSku(product) &&
        getProductDeliveryChannel(product) &&
        getProductLineNumber(product) != null
    )
    .map((product) => ({
      catalogNumber: getProductSku(product),
      deliveryChannel: getProductDeliveryChannel(product),
      availabilityDate: getProductAvailabilityDate(product),
      stockStatus: getProductStockStatus(product),
      quantity: product.quantity,
      lineNumber: String(getProductLineNumber(product)),
    })),
});

export const mergeCRDRangeData = (
  products: OrderPreviewProduct[] = [],
  response?: CRDRangeResponse
) => {
  const rangeMap = new Map<string, CRDRangeResponseItem>();
  (response?.crdRanges || []).forEach((item) => {
    const key = `${item.catalogNumber || ""}${item.deliveryChannel || ""}${normalizeLineNumber(item.lineNumber) || ""}`;
    rangeMap.set(key, { ...item, lineNumber: normalizeLineNumber(item.lineNumber) });
  });

  return products.map((product) => {
    const key = `${getProductSku(product) || ""}${getProductDeliveryChannel(product) || ""}${getProductLineNumber(product) || ""}`;
    const matchedRange = rangeMap.get(key);
    if (!matchedRange) {
      return {
        ...product,
        ...(getProductLineNumber(product) ? { lineNumber: getProductLineNumber(product) } : { lineNumber: null }),
      };
    }
    return {
      ...product,
      lineNumber: getProductLineNumber(product),
      CRD_Range: matchedRange,
      crd: matchedRange.defaultDate || product.crd,
    };
  });
};

export const applyCRDSelection = (
  products: OrderPreviewProduct[] = [],
  payload: CRDSelectionPayload
) =>
  products.map((product) => {
    const key = getOrderLineKey(product);
    return {
      ...product,
      ...(getProductLineNumber(product) ? { lineNumber: getProductLineNumber(product) } : { lineNumber: null }),
      crd: payload.selectedLinesMap[key] || product.crd,
    };
  });

export const isDateInCRDRange = (
  range: any,
  dateStr?: string,
  holidays: CRDHolidayItem[] | string[] = []
) => {
  if (!range || range.status !== "eligible" || !dateStr) {
    return false;
  }
  if (isHolidayDisabled(dateStr, holidays)) {
    return false;
  }
  const current = new Date(dateStr).getTime();
  const start = range.startDate ? new Date(range.startDate).getTime() : null;
  const end = range.endDate ? new Date(range.endDate).getTime() : null;
  if (Number.isNaN(current)) {
    return false;
  }
  if (start && current < start) {
    return false;
  }
  if (end && current > end) {
    return false;
  }
  return true;
};

export const getCRDRangeText = (range: any) => {
  if (!range || range.status !== "eligible") {
    return "";
  }
  if (!range.startDate || !range.endDate) {
    return "";
  }
  return `可选范围 ${range.startDate} ~ ${range.endDate}`;
};
